import type { KeyboardEvent } from 'react';

/**
 * Keyboard model for an ARIA tree (WAI-ARIA APG "tree view"), shared by the
 * admin folder tree and the document tree. Attach to the `role="tree"` element's
 * onKeyDown; the component keeps a roving tabindex (exactly one treeitem has
 * tabIndex=0 — the active one — and updates it from each item's onFocus).
 *
 *   ↓ / ↑      next / previous visible item
 *   →          closed branch: open it · open branch: first child
 *   ←          open branch: close it · otherwise: the parent item
 *   Home / End first / last visible item
 *   Enter / Space  activate (select) the item
 *   a printable character: the next item whose label starts with it
 *
 * Collapsed groups are not rendered, so every [role=treeitem] in the DOM is
 * visible, in tab order.
 */
export interface TreeKeyHandlers {
  /** Open (true) or close (false) a branch item. */
  setExpanded: (item: HTMLElement, open: boolean) => void;
  /** Select / activate an item (Enter, Space). */
  activate: (item: HTMLElement) => void;
}

function itemsOf(tree: HTMLElement): HTMLElement[] {
  return Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]'));
}

/** The treeitem that owns the group an item sits in (its parent), if any. */
export function parentTreeItem(item: HTMLElement): HTMLElement | null {
  const group = item.parentElement?.closest('[role="group"]');
  const owner = group?.parentElement;
  return owner?.querySelector<HTMLElement>(':scope > [role="treeitem"]') ?? null;
}

/** The first item in this branch item's group, if open and non-empty. */
function firstChildItem(item: HTMLElement): HTMLElement | null {
  const group = item.parentElement?.querySelector<HTMLElement>(':scope > [role="group"]');
  return group?.querySelector<HTMLElement>('[role="treeitem"]') ?? null;
}

export function handleTreeKeyDown(e: KeyboardEvent<HTMLElement>, h: TreeKeyHandlers): void {
  const tree = e.currentTarget;
  const item = (e.target as HTMLElement).closest<HTMLElement>('[role="treeitem"]');
  if (!item || !tree.contains(item)) return;
  const items = itemsOf(tree);
  const at = items.indexOf(item);
  const focus = (el: HTMLElement | null | undefined) => {
    if (el) {
      e.preventDefault();
      el.focus();
    }
  };
  const expanded = item.getAttribute('aria-expanded');

  switch (e.key) {
    case 'ArrowDown':
      focus(items[at + 1]);
      break;
    case 'ArrowUp':
      focus(items[at - 1]);
      break;
    case 'Home':
      focus(items[0]);
      break;
    case 'End':
      focus(items[items.length - 1]);
      break;
    case 'ArrowRight':
      if (expanded === 'false') {
        e.preventDefault();
        h.setExpanded(item, true);
      } else if (expanded === 'true') {
        focus(firstChildItem(item));
      }
      break;
    case 'ArrowLeft':
      if (expanded === 'true') {
        e.preventDefault();
        h.setExpanded(item, false);
      } else {
        focus(parentTreeItem(item));
      }
      break;
    case 'Enter':
    case ' ':
      e.preventDefault();
      h.activate(item);
      break;
    default:
      if (e.key.length === 1 && /\S/.test(e.key) && !e.ctrlKey && !e.metaKey && !e.altKey) {
        const ch = e.key.toLowerCase();
        for (let i = 1; i <= items.length; i++) {
          const cand = items[(at + i) % items.length];
          const label = (cand?.getAttribute('data-label') ?? cand?.textContent ?? '').trim();
          if (label.toLowerCase().startsWith(ch)) {
            focus(cand);
            break;
          }
        }
      }
  }
}
